import React from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatMoney } from '@/lib/api/manufacturing';
import { splitManufacturingSimulation } from '@/lib/api/manufacturingSimulation';
import { splitSimulationPreview } from '@/lib/simulationSplit';

function localDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function Summary({ title, data }) {
  return <section className="rounded-lg border bg-gray-50 p-3">
    <h3 className="font-semibold">{title}</h3>
    <p>{data.articles} {data.articles === 1 ? 'artículo' : 'artículos'} · {data.quantity} prendas</p>
    <dl className="mt-2 grid grid-cols-2 gap-1 text-sm">
      <dt>Materiales</dt><dd className="text-right">{formatMoney(data.material)}</dd>
      <dt>Procesos</dt><dd className="text-right">{formatMoney(data.process)}</dd>
      <dt className="font-semibold">Coste directo</dt><dd className="text-right font-semibold">{formatMoney(data.total)}</dd>
      <dt>Gastos generales aparte</dt><dd className="text-right">{formatMoney(data.overhead)}</dd>
    </dl>
  </section>;
}

// Mounted with a simulation-specific key so selections never leak between runs.
export function SimulationSplitDialog({ simulation, onClose, onCompleted, onOpenDestination }) {
  const [selected, setSelected] = React.useState(new Set());
  const [search, setSearch] = React.useState('');
  const [description, setDescription] = React.useState(`${simulation.description} · Parte 2`);
  const [date, setDate] = React.useState(localDate);
  const [notes, setNotes] = React.useState(simulation.notes || '');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const submitting = React.useRef(false);
  const request = React.useRef(null);
  const items = simulation.items || [];
  const visible = items.filter(item => `${item.product_code_snapshot} ${item.description_snapshot}`.toLocaleLowerCase('es').includes(search.trim().toLocaleLowerCase('es')));
  const preview = splitSimulationPreview(items, selected);
  const allVisible = visible.length > 0 && visible.every(item => selected.has(item.id));
  const someVisible = visible.some(item => selected.has(item.id));
  const valid = description.trim() && date && preview.moving.articles > 0 && preview.remaining.articles > 0;
  const toggle = (ids, checked) => setSelected(current => {
    const next = new Set(current);
    ids.forEach(id => checked ? next.add(id) : next.delete(id));
    return next;
  });
  const submit = async (event) => {
    event.preventDefault();
    if (!valid || submitting.current || result) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    const payload = { sourceId: simulation.id, expectedVersion: simulation.content_version,
      itemIds: [...selected].sort(), description, simulationDate: date, notes };
    const fingerprint = JSON.stringify(payload);
    if (request.current?.fingerprint !== fingerprint) request.current = { fingerprint, operationId: crypto.randomUUID() };
    try {
      const response = await splitManufacturingSimulation({ ...payload, operationId: request.current.operationId });
      if (response.error) throw response.error;
      setResult(response.data);
      onCompleted?.(response.data);
    } catch (failure) {
      setError(failure.message || 'No se pudo dividir la simulación. Puedes volver a intentarlo.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  return <Dialog open onOpenChange={open => { if (!open && !submitting.current) onClose(); }}>
    <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-6xl flex-col overflow-hidden">
      <DialogHeader className="shrink-0">
        <DialogTitle>Dividir simulación</DialogTitle>
        <DialogDescription>{simulation.description}. Los artículos seleccionados se trasladarán completos a una nueva simulación, con todas sus tallas, colores y costes guardados.</DialogDescription>
      </DialogHeader>
      {result ? <div className="min-h-0 overflow-y-auto space-y-4">
        <p role="status">División completada. Ambas simulaciones quedan activas y puedes convertirlas en órdenes en momentos diferentes.</p>
        <div className="grid gap-3 sm:grid-cols-2"><Summary title="Permanecen en la original" data={preview.remaining} /><Summary title="Pasan a la nueva" data={preview.moving} /></div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cerrar</Button><Button onClick={() => onOpenDestination(result.destination_id)}>Abrir nueva simulación</Button></div>
      </div> : <form onSubmit={submit} className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <div className="min-h-0 overflow-y-auto pr-1"><fieldset disabled={busy} className="min-w-0 space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_210px]">
            <label className="text-sm font-medium">Descripción de la nueva simulación<Input required value={description} onChange={event => setDescription(event.target.value)} /></label>
            <label className="text-sm font-medium">Fecha de la nueva simulación<Input required type="date" value={date} onChange={event => setDate(event.target.value)} /></label>
          </div>
          <label className="block text-sm font-medium">Observaciones de la nueva simulación<Input value={notes} onChange={event => setNotes(event.target.value)} /></label>
          <div className="grid gap-3 sm:grid-cols-2" aria-live="polite"><Summary title="Permanecen en la original" data={preview.remaining} /><Summary title="Pasan a la nueva" data={preview.moving} /></div>
          <label className="block text-sm font-medium">Buscar artículos<Input type="search" placeholder="Código o descripción" value={search} onChange={event => setSearch(event.target.value)} /></label>
          <p className="text-sm text-gray-600" role="status">{selected.size} de {items.length} artículos seleccionados en total · {visible.length} visibles. La búsqueda conserva la selección.</p>
          <div className="max-h-[35vh] w-full min-w-0 overflow-auto rounded border">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="sticky top-0 bg-gray-100 text-left"><tr>
                <th className="p-2"><label><input type="checkbox" checked={allVisible} disabled={!visible.length} ref={node => { if (node) node.indeterminate = someVisible && !allVisible; }} onChange={event => toggle(visible.map(item => item.id), event.target.checked)} /> Seleccionar visibles</label></th>
                <th className="p-2">Artículo</th><th className="p-2">Temporada</th><th className="p-2 text-right">Prendas</th><th className="p-2 text-right">Materiales</th><th className="p-2 text-right">Procesos</th><th className="p-2 text-right">Coste directo</th>
              </tr></thead>
              <tbody>{visible.map(item => <tr key={item.id} className={selected.has(item.id) ? 'border-t bg-blue-50' : 'border-t'}>
                <td className="p-2"><input type="checkbox" aria-label={`Trasladar ${item.product_code_snapshot}`} checked={selected.has(item.id)} onChange={event => toggle([item.id], event.target.checked)} /></td>
                <td className="p-2"><b>{item.product_code_snapshot}</b><span className="block">{item.description_snapshot}</span></td>
                <td className="p-2">{item.season_snapshot || '—'}</td><td className="p-2 text-right">{item.total_quantity}</td><td className="p-2 text-right">{formatMoney(item.total_material_cost)}</td><td className="p-2 text-right">{formatMoney(item.total_process_cost)}</td><td className="p-2 text-right">{formatMoney(item.total_cost)}</td>
              </tr>)}{!visible.length && <tr><td colSpan={7} className="p-4 text-center">No hay artículos que coincidan con la búsqueda.</td></tr>}</tbody>
            </table>
          </div>
        </fieldset></div>
        {!preview.remaining.articles && <p className="text-sm text-amber-800">Debe permanecer al menos un artículo en la simulación original.</p>}
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex shrink-0 flex-wrap justify-end gap-2"><Button type="button" variant="outline" disabled={busy} onClick={onClose}>Cancelar</Button><Button type="submit" disabled={!valid || busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crear nueva simulación y trasladar</Button></div>
      </form>}
    </DialogContent>
  </Dialog>;
}
